// sectors.js — one place that defines how a sector looks everywhere in the
// CRM: label, colours and icon. Used by the Quote Log (Type column, mobile
// cards, detail panel, Send to Client), the calculator category picker and
// the Schedule's Gantt bars.
//
// Icons are the website's own sector icons (sublimeprecision.co.uk sector
// grid): inline 24x24 outline SVGs, 1.6 stroke (1.5 for the cog), round caps
// and joins - Lucide-style drawings, but not a stock set, so they're copied
// here exactly rather than pulling in an icon library. The car and medical
// cross aren't on the website and are drawn in the same style.

import React from "react";

// Inner SVG markup per icon (static strings from this file only).
const ICONS = {
  cylinder: { sw: 1.6, inner: "<ellipse cx=\"12\" cy=\"5\" rx=\"6\" ry=\"2.2\"/><path d=\"M6 5v14c0 1.2 2.7 2.2 6 2.2s6-1 6-2.2V5\"/><path d=\"M6 11c0 1.2 2.7 2.2 6 2.2s6-1 6-2.2\"/>" },
  bolt: { sw: 1.6, inner: "<path d=\"M13 2 4 14h6l-1 8 9-12h-6z\"/>" },
  factory: { sw: 1.6, inner: "<path d=\"M3 21V9l6 4V9l6 4V5h3v16z\"/><path d=\"M3 21h18\"/><path d=\"M6.5 21v-3M11 21v-3M15.5 21v-3\"/>" },
  cog: { sw: 1.5, inner: "<path d=\"M19.4 13a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V20a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 8 18.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H2a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 3.6 8a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H8a1.65 1.65 0 0 0 1-1.51V2a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V8a1.65 1.65 0 0 0 1.51 1H22a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z\"/><circle cx=\"12\" cy=\"12\" r=\"3\"/>" },
  wrench: { sw: 1.6, inner: "<path d=\"M14 6a3 3 0 0 0-4 4l-7 7 3 3 7-7a3 3 0 0 0 4-4l-2 2-2-2 2-2z\"/>" },
  windTurbine: { sw: 1.6, inner: "<path d=\"M10 22h4\"/><path d=\"M12 22V12\"/><circle cx=\"12\" cy=\"11\" r=\"1\"/><path d=\"M12 10V4\"/><path d=\"M12.9 11.5 18 14.5\"/><path d=\"M11.1 11.5 6 14.5\"/>" },
  wheat: { sw: 1.6, inner: "<path d=\"M12 22V9\"/><path d=\"M12 9c0-2 1.4-3.4 3.4-3.4C15.4 7.6 14 9 12 9Z\"/><path d=\"M12 9c0-2-1.4-3.4-3.4-3.4C8.6 7.6 10 9 12 9Z\"/><path d=\"M12 13c0-2 1.4-3.4 3.4-3.4C15.4 11.6 14 13 12 13Z\"/><path d=\"M12 13c0-2-1.4-3.4-3.4-3.4C8.6 11.6 10 13 12 13Z\"/><path d=\"M12 17c0-2 1.4-3.4 3.4-3.4C15.4 15.6 14 17 12 17Z\"/><path d=\"M12 17c0-2-1.4-3.4-3.4-3.4C8.6 15.6 10 17 12 17Z\"/>" },
  sailboat: { sw: 1.6, inner: "<path d=\"M12 3v9\"/><path d=\"M12.5 5 17 11h-4.5z\"/><path d=\"M4 14h16l-1.6 4.6a2 2 0 0 1-1.9 1.4H7.5a2 2 0 0 1-1.9-1.4z\"/>" },
  plane: { sw: 1.6, inner: "<path d=\"M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 4.3 3.5c.4.3.8.4 1.3.2l.5-.2c.4-.3.6-.7.5-1.2Z\"/>" },
  server: { sw: 1.6, inner: "<rect x=\"3\" y=\"4\" width=\"18\" height=\"7\" rx=\"1.5\"/><rect x=\"3\" y=\"13\" width=\"18\" height=\"7\" rx=\"1.5\"/><path d=\"M7 7.5h.01\"/><path d=\"M7 16.5h.01\"/><path d=\"M11 7.5h6\"/><path d=\"M11 16.5h6\"/>" },
  car: { sw: 1.6, inner: "<path d=\"M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2\"/><circle cx=\"7\" cy=\"17\" r=\"2\"/><path d=\"M9 17h6\"/><circle cx=\"17\" cy=\"17\" r=\"2\"/>" },
  cross: { sw: 1.6, inner: "<path d=\"M9 3.5h6v5.5h5.5v6H15v5.5H9V15H3.5V9H9z\"/>" },
  building: { sw: 1.6, inner: "<rect x=\"4.5\" y=\"3\" width=\"15\" height=\"18\" rx=\"1.5\"/><path d=\"M9.5 21v-3.5h5V21\"/><path d=\"M8.5 7h.01M12 7h.01M15.5 7h.01M8.5 10.5h.01M12 10.5h.01M15.5 10.5h.01M8.5 14h.01M15.5 14h.01\"/>" },
  tag: { sw: 1.6, inner: "<path d=\"M3 12V4.5A1.5 1.5 0 0 1 4.5 3H12l9 9-9 9z\"/><circle cx=\"7.5\" cy=\"7.5\" r=\"1.2\"/>" },
};

// tint = pill background on light pages, ink = icon + label on light pages
// (a darker shade of the same hue). On the dark calculators the pill uses
// the ink colour at low opacity with the tint as text, so it stays readable.
const HUES = {
  green:  { tint: "#E3F2E8", ink: "#2E7148" },
  purple: { tint: "#EEE7F8", ink: "#6A4AA0" },
  slate:  { tint: "#E5EBF4", ink: "#3C5A80" },
  teal:   { tint: "#DFF1EF", ink: "#1E716A" },
  sky:    { tint: "#E1EFFA", ink: "#28689A" },
  amber:  { tint: "#FBEFD9", ink: "#94600E" },
  red:    { tint: "#FAE5E4", ink: "#A93A36" },
  olive:  { tint: "#EDF1DC", ink: "#5E6A1F" },
  indigo: { tint: "#E7E8F7", ink: "#484CA0" },
  grey:   { tint: "#ECEEF2", ink: "#5B6474" },
  steel:  { tint: "#E4E9EF", ink: "#4A5C70" },
  rose:   { tint: "#FBE7EC", ink: "#A33F5E" },
};

// key -> look. `match` lists every way the sector can be written: the
// Category column value, the calculator's service value, and the website's
// sector name, so old and new spellings all land on the same style.
const SECTORS = [
  { key: "food",         label: "Food",          icon: "cylinder",    hue: "green",  match: ["food", "food production"] },
  // Same green Food style, keeping its own name on the pill.
  { key: "catering",     label: "Catering & Hospitality", icon: "cylinder", hue: "green", match: ["catering", "catering & hospitality"] },
  { key: "machinery",    label: "Machinery",     icon: "cog",         hue: "purple", match: ["machinery", "production machinery"] },
  { key: "industrial",   label: "Industrial",    icon: "factory",     hue: "slate",  match: ["industrial", "manufacturing"] },
  { key: "marine",       label: "Marine",        icon: "sailboat",    hue: "teal",   match: ["marine", "marine & transport"] },
  { key: "aviation",     label: "Aviation",      icon: "plane",       hue: "sky",    match: ["aviation", "aerospace & aviation", "aerospace"] },
  { key: "construction", label: "Construction",  icon: "wrench",      hue: "amber",  match: ["construction", "engineering & fabrication", "engineering"] },
  { key: "automotive",   label: "Automotive",    icon: "car",         hue: "red",    match: ["automotive"] },
  // Current CRM categories not in the brief - website icon where there is one.
  { key: "agriculture",  label: "Agriculture",   icon: "wheat",       hue: "olive",  match: ["agriculture"] },
  { key: "data_centres", label: "Data Centres",  icon: "server",      hue: "indigo", match: ["data centres", "data_centres", "data centers"] },
  { key: "facilities",   label: "Facilities & Leisure", icon: "building", hue: "steel", match: ["facilities", "facilities & leisure"] },
  { key: "healthcare",   label: "Healthcare",    icon: "cross",       hue: "rose",   match: ["healthcare"] },
  // Future website sectors.
  { key: "electrical",   label: "Electrical Equipment", icon: "bolt",        hue: "amber", match: ["electrical", "electrical equipment"] },
  { key: "power",        label: "Power & Energy",       icon: "windTurbine", hue: "teal",  match: ["power", "power & energy", "energy"] },
];
const FALLBACK = { key: "other", label: "", icon: "tag", hue: "grey" };

// Sector for a Category / service value (case-insensitive), or the neutral
// fallback (keeping the original text as its label) for anything unknown.
export function getSector(value) {
  const v = String(value || "").trim().toLowerCase();
  const s = SECTORS.find(x => x.key === v || x.label.toLowerCase() === v || x.match.includes(v));
  const base = s || { ...FALLBACK, label: String(value || "").trim() };
  return { ...base, ...HUES[base.hue] };
}

// strokeWidth: drawn a little heavier than the website's 1.6 so the icons stay
// legible at pill size.
export function SectorIcon({ icon, size = 14, strokeWidth = 1.8, style }) {
  const i = ICONS[icon] || ICONS.tag;
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={strokeWidth || i.sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      style={{ flexShrink: 0, display: "block", ...style }}
      dangerouslySetInnerHTML={{ __html: i.inner }}
    />
  );
}

// The sector pill - same shape as the Quote Log's existing Type pill (11px
// semibold, fully rounded) and about the same height, with an 18px icon.
// dark: for the dark calculator pages.
export function SectorPill({ category, dark, size = "md", style }) {
  const s = getSector(category);
  if (!s.label) return null;
  const sm = size === "sm";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: sm ? 4 : 5, verticalAlign: "middle",
      // Larger icon, less vertical padding: the pill stays about the same height.
      fontSize: sm ? 10.5 : 11, fontWeight: 600, padding: sm ? "1px 7px 1px 5px" : "1px 9px 1px 6px", borderRadius: 999,
      background: dark ? s.ink + "40" : s.tint,
      color: dark ? s.tint : s.ink,
      whiteSpace: "nowrap", lineHeight: 1.4,
      ...style,
    }}>
      <SectorIcon icon={s.icon} size={sm ? 15 : 18} />
      {/* Own span so a width-capped pill (style.maxWidth) ends in "…". */}
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>{s.label}</span>
    </span>
  );
}
